import React from 'react'
import { Card, Typography } from '@surecompanies/core_components'
import { ClockCircleOutlined, TeamOutlined } from '@ant-design/icons'
import type { GetCarrierDetailResponseDto } from '../../../../api/dto/get-payment-bot-dashboard.dto'
import { formatDuration, rateColor } from '../../shared/utils'
import styles from './CarrierDetail.module.scss'

const { Text } = Typography

interface Props {
  data: GetCarrierDetailResponseDto
}

// Carrier-detail KPI row: total benefits, process status, and avg processing time.
export function CarrierDetailKpiRow({ data }: Props) {
  // Use completedCount+failedCount as denominator (= distinct_benefits with tasks),
  // which matches firstAttemptRate's own denominator. totalBenefits includes PENDING.
  const firstSucceeded = Math.round(
    (data.firstAttemptRate * (data.completedCount + data.failedCount)) / 100
  )
  const rescueRate = Math.max(
    0,
    Math.round((data.eventualRate - data.firstAttemptRate) * 10) / 10
  )
  const rescuedCases = Math.max(0, data.completedCount - firstSucceeded)

  const successPct = data.eventualRate
  const firstAttemptPct = data.firstAttemptRate
  const secondAttemptPct = rescueRate
  const failedPct =
    data.completedCount + data.failedCount === 0
      ? 0
      : Math.max(0, Math.round((100 - successPct) * 10) / 10)

  return (
    <div className={styles.kpiCardRow}>
      <Card bodyStyle={{ padding: 20 }}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiCardBody}>
            <div className={styles.kpiCardLabel}>Total Benefits</div>
            <div className={styles.kpiCardValue}>
              {data.totalBenefits.toLocaleString()}
            </div>
            <div className={styles.kpiCardSub}>
              {data.pendingCount > 0
                ? `${data.pendingCount.toLocaleString()} pending`
                : 'All processed'}
            </div>
          </div>
          <TeamOutlined
            className={`${styles.kpiCardIcon} ${styles.iconCyan}`}
          />
        </div>
      </Card>

      <Card bodyStyle={{ padding: 20 }}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiCardBody}>
            <div className={styles.kpiCardLabel}>Bot Process Status</div>
            <div className={styles.kpiCardRow2}>
              <Text>Success</Text>
              <Text strong style={{ color: rateColor(successPct) }}>
                {data.completedCount.toLocaleString()} ({successPct}%)
              </Text>
            </div>
            <div className={styles.kpiCardRow2Sub}>
              <Text type="secondary">First Attempt</Text>
              <Text type="secondary">
                {firstSucceeded.toLocaleString()} ({firstAttemptPct}%)
              </Text>
            </div>
            <div className={styles.kpiCardRow2Sub}>
              <Text type="secondary">2nd+ Attempts</Text>
              <Text type="secondary">
                {rescuedCases.toLocaleString()} ({secondAttemptPct}%)
              </Text>
            </div>
            <div className={styles.kpiCardRow2Failed}>
              <Text>Failed</Text>
              <Text strong className={styles.textDanger}>
                {data.failedCount.toLocaleString()} ({failedPct}%)
              </Text>
            </div>
          </div>
        </div>
      </Card>

      <Card bodyStyle={{ padding: 20 }}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiCardBody}>
            <div className={styles.kpiCardLabel}>Avg Processing Time</div>
            <div className={styles.kpiCardValue}>
              {formatDuration(data.medianProcessingTimeSec)}
            </div>
            <div className={styles.kpiCardSub}>Median per benefit event</div>
          </div>
          <ClockCircleOutlined
            className={`${styles.kpiCardIcon} ${styles.iconOrange}`}
          />
        </div>
      </Card>
    </div>
  )
}
